import { useState, useEffect, useContext } from "react";
import { useSession } from "next-auth/react";
import { Text, Box, Button, Center, useColorModeValue } from "@chakra-ui/react";
import { useFirebaseUser } from "utils/useFirebaseUser";
import { firebaseClient } from "firebaseClient";
import { getAccount } from "utils/sessionHelper";
import { isTweetFromAccount } from "controllers/schedule";
import { Timeline } from "components/timeline";
import { convertToAccountTimezone } from "utils/helpers";
import { TweetBar } from "./tweet-bar";
import { TweetContext } from "context/tweetContext";
import { useMiscellaneousContext } from "context/miscellaneousContext";
import { textStyle } from "theme/names";

export function PublishedPanel() {
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [tweets, setTweets] = useState<any[]>([]);
  const [slots, setSlots] = useState<any[]>([]);
  const isAuthenticated = useFirebaseUser();
  const { data: session } = useSession() ?? {}
  const tweetContext: any = useContext(TweetContext);
  const miscellaneousContext: any = useMiscellaneousContext();
  const tweetbarBg = useColorModeValue("green.50", "green.900");
  const { timezoneDiff } = convertToAccountTimezone(
    new Date(),
    getAccount(session)?.timezone
  );

  useEffect(() => {
    if (isAuthenticated) {
      getPublishedTweets();
    }
  }, [isAuthenticated, miscellaneousContext.refreshPublishedPanel]);

  const createTweetSlots = (tweets: any[]) => {
    let slots: any[] = [];

    tweets.forEach((tweet) => {
      const dateSent = new Date(
        tweet.dateSent.toDate().getTime() - 1000 * 60 * timezoneDiff
      );

      let midDay = new Date(
        dateSent.getFullYear(),
        dateSent.getMonth(),
        dateSent.getDate(),
        12,
        0,
        0,
        0
      );
      midDay = convertToAccountTimezone(
        midDay,
        getAccount(session)?.timezone
      )?.date;
      const index = slots.findIndex(
        (s) => s.time.getTime() === midDay.getTime()
      );
      let newSlot = slots[index];
      if (!newSlot) {
        newSlot = {
          time: midDay,
          slotsOfTheDay: [],
        };
      }

      newSlot.slotsOfTheDay.push({ time: dateSent, tweet });

      if (index !== -1) {
        slots[index] = newSlot;
        return;
      }

      slots.push(newSlot);
    });

    setSlots(slots);
  };

  const getPublishedTweets = async (type?: "loadMore") => {
    if (!isAuthenticated) {
      return;
    }

    const LOAD_BATCH = 30;
    setIsLoading(true);

    let tweetsCopy = type === "loadMore" ? [...tweets] : [];
    const db = firebaseClient.firestore();

    let query = db
      .collection("users")
      .doc(getAccount(session).id)
      .collection("tweets")
      .where("status", "==", "sent")
      .where("idAccount", "==", getAccount(session).idAccount)
      .orderBy("dateSent", "desc");

    if (type === "loadMore" && tweetsCopy.length > 0) {
      query = query.startAfter(tweetsCopy[tweetsCopy?.length - 1]?.dateSent);
    }

    const docs = await query.limit(LOAD_BATCH).get();

    docs.forEach((doc) => {
      const data = doc.data();

      if (isTweetFromAccount(data, session)) {
        tweetsCopy.push(data);
      }
    });

    // tweetsCopy = tweetsCopy.filter((t) => t.isDeleted !== true);

    setTweets(tweetsCopy);
    createTweetSlots(tweetsCopy);
    setIsLoading(false);

    if (docs?.docs?.length === LOAD_BATCH) {
      setHasMore(true);
    } else {
      setHasMore(false);
    }
  };

  if (!isLoading && tweets.length === 0) {
    return (
      <Center mt="44">
        <Box textAlign="center">
          <Text textStyle={textStyle["body.medium.light"]}>No posts published via Taplio</Text>
          <Button
            variant="primary"
            // bg="blue.700"
            // colorScheme="blue"
            // color="white"
            // _hover={{ bg: "blue.600" }}
            mt="4"
            onClick={() => tweetContext.open()}
          >
            Write a post
          </Button>
        </Box>
      </Center>
    );
  }

  return (
    <Box>
      <Timeline
        slots={slots}
        render={(slotItem) => (
          <TweetBar
            key={slotItem.time?.getTime()}
            type="published"
            tweet={slotItem.tweet}
            time={slotItem.time}
            bg={tweetbarBg}
          />
        )}
      />
      {tweets.length > 0 && hasMore && (
        <Box textAlign="center" mt="16">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => getPublishedTweets("loadMore")}
            isLoading={isLoading}
          >
            Load more
          </Button>
        </Box>
      )}
    </Box>
  );
}
